import type { ContextSlug } from "@/entities/context";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";

import { useGetCurrentUser } from "@/entities/current-user";
import {
  findOrganizationById,
  findOrganizationBySlug,
  useGetOrganization,
} from "@/entities/organization";
import { useGetOrganizations } from "@/entities/organizations";
import { useGetSettings } from "@/entities/settings";
import { useAppNotification } from "@/modules/app-notification";
import { useContextParam } from "@/services/router";

import { ORGANIZATION_CONTEXT_STALE_TIME } from "./organization-context.constant";
import { convertContextPath } from "./organization-context.helper";

export const useOrganizationContext = () => {
  const contextParam = useContextParam();
  const { settings, isLoading } = useGetSettings();

  const contextSlug: ContextSlug | undefined =
    contextParam ?? settings?.context ?? undefined;

  return {
    contextSlug,
    isLoading,
  };
};

export const useOrganizationContextList = () => {
  const { organizations, isLoading } = useGetOrganizations({
    params: { sort: "name", "page[size]": 100 },
    staleTime: ORGANIZATION_CONTEXT_STALE_TIME,
    persist: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
  });

  return {
    organizations,
    listLength: organizations.length,
    isLoading,
  };
};

export const useOrganizationContextId = () => {
  const { contextSlug, isLoading: isContextLoading } = useOrganizationContext();
  const { organizations, isLoading: isListLoading } =
    useOrganizationContextList();

  const contextId = findOrganizationBySlug(contextSlug, organizations)?.id;

  return {
    contextId,
    isLoading: isContextLoading || isListLoading,
  };
};

export const useOrganizationContextInvalid = () => {
  const { contextId, isLoading } = useOrganizationContextId();
  const { fallbackOrganization } = useOrganizationContextFallback();

  const isInvalid = !isLoading && !contextId;
  const redirectContext = isInvalid ? fallbackOrganization?.slug : undefined;

  return {
    isInvalid,
    isLoading,
    redirectContext,
  };
};

export const useOrganizationContextFallback = () => {
  const { settings } = useGetSettings();
  const { currentUser } = useGetCurrentUser();
  const { organizations } = useOrganizationContextList();

  const settingsOrganization = findOrganizationBySlug(
    settings?.context,
    organizations
  );

  const userOrganization = findOrganizationById(
    currentUser?.organization_id,
    organizations
  );

  const fallbackOrganization = settingsOrganization ?? userOrganization;

  return {
    fallbackOrganization,
    settingsOrganization,
    userOrganization,
  };
};

export const useOrganizationContextDetail = () => {
  const { contextId, isLoading: isContextLoading } = useOrganizationContextId();

  const { organization, isLoading: isOrganizationLoading } = useGetOrganization(
    contextId,
    {
      staleTime: ORGANIZATION_CONTEXT_STALE_TIME,
      persist: true,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true,
    }
  );

  return {
    contextOrganization: organization,
    isLoading: isContextLoading || isOrganizationLoading,
  };
};

export const useOrganizationContextNavigate = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const navigateToContext = (context: ContextSlug) => {
    const newPath = convertContextPath({ pathname, context });
    if (newPath) navigate(newPath);
    return { hasNavigate: !!newPath };
  };

  return {
    navigateToContext,
  };
};

export const useOrganizationCustomContext = () => {
  const { contextSlug } = useOrganizationContext();
  const { listLength } = useOrganizationContextList();

  const showCustomContext = (customContext?: ContextSlug | null): boolean => {
    return !!(customContext && customContext !== contextSlug && listLength > 1);
  };

  return {
    showCustomContext,
  };
};

export const useOrganizationCustomContextNotify = () => {
  const { t } = useTranslation();
  const { notify } = useAppNotification();
  const { showCustomContext } = useOrganizationCustomContext();

  const notifyContextChange = (customContext?: ContextSlug | null) => {
    if (showCustomContext(customContext)) {
      notify.info({ title: t("context:toaster.contextUpdated") });
    }
  };

  return {
    notifyContextChange,
  };
};
